import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, CalendarDays, Crown, BookOpen, Plus, Check, X, Trash2,
  BadgeCheck, Inbox, Search, KeyRound, Globe2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import RegionSelect from '../world/RegionSelect';
import AdminCountryProfile from './AdminCountryProfile';
import { API } from '../../lib/api';

/**
 * Správa katalogu Bloom World – organizace, akce, soutěže, rozcestníky.
 *
 * Všechny čtyři druhy mají na serveru společné API, takže i tady stačí jedna
 * obrazovka. Co se u druhu liší, drží tabulka DRUHY: jen políčka navíc.
 */

const DRUHY = [
  {
    key: 'organizations', icon: Building2, label: 'Organizace',
    pole: [
      { name: 'areas', label: 'Oblasti činnosti', typ: 'seznam' },
      { name: 'services', label: 'Co nabízí', typ: 'seznam' },
    ],
  },
  {
    key: 'events', icon: CalendarDays, label: 'Akce',
    pole: [
      { name: 'starts_at', label: 'Začátek', typ: 'datum', povinne: true },
      { name: 'ends_at', label: 'Konec', typ: 'datum' },
      { name: 'format', label: 'Forma', typ: 'vyber',
        moznosti: [['offline', 'Osobně'], ['online', 'Online'], ['hybrid', 'Obojí']] },
      { name: 'url', label: 'Odkaz na akci' },
      { name: 'organizer_name', label: 'Pořadatel' },
    ],
  },
  {
    key: 'pageants', icon: Crown, label: 'Soutěže',
    pole: [
      { name: 'organizer_name', label: 'Pořadatel' },
      { name: 'starts_at', label: 'Termín', typ: 'datum' },
      { name: 'registration_deadline', label: 'Uzávěrka přihlášek', typ: 'datum' },
      { name: 'registration_url', label: 'Odkaz na přihlášku' },
      { name: 'eligibility', label: 'Kdo se může přihlásit' },
      { name: 'scope', label: 'Rozsah', typ: 'vyber',
        moznosti: [['local', 'Místní'], ['national', 'Celostátní'], ['international', 'Mezinárodní']] },
      { name: 'edition_status', label: 'Stav ročníku', typ: 'vyber',
        moznosti: [['upcoming', 'Chystá se'], ['registration_open', 'Přihlášky otevřené'],
                   ['registration_closed', 'Přihlášky uzavřené'], ['completed', 'Proběhlo']] },
    ],
  },
  {
    key: 'resources', icon: BookOpen, label: 'Rozcestník',
    pole: [
      { name: 'scope', label: 'Platí pro', typ: 'vyber',
        moznosti: [['global', 'Celý svět'], ['country', 'Zemi'], ['region', 'Oblast'], ['city', 'Město']] },
      { name: 'topic', label: 'Téma' },
      { name: 'body', label: 'Text', typ: 'dlouhy' },
      { name: 'links', label: 'Odkazy', typ: 'seznam' },
    ],
  },
];

const prazdnyFormular = () => ({
  name: '', description: '', country_iso2: '', region_id: '', city_name: '', address: '',
  email: '', phone: '', website: '', categories: '', source: '', details: {},
});

export function AdminWorldTab() {
  const [druh, setDruh] = useState('organizations');
  const [zaznamy, setZaznamy] = useState([]);
  const [fronta, setFronta] = useState([]);
  const [zadosti, setZadosti] = useState([]);
  // Katalog a profily zemí jsou dvě různé práce nad týmiž zeměmi.
  const [rezim, setRezim] = useState('katalog');
  const [zeme, setZeme] = useState([]);
  const [nacita, setNacita] = useState(false);
  const [hledani, setHledani] = useState('');
  const [formularOtevren, setFormularOtevren] = useState(false);
  const [formular, setFormular] = useState(prazdnyFormular());
  const [uklada, setUklada] = useState(false);

  const aktualni = DRUHY.find((d) => d.key === druh);

  const nactiZaznamy = useCallback(async (kind) => {
    setNacita(true);
    try {
      const { data } = await axios.get(`${API}/admin/directory/${kind}`);
      setZaznamy(data);
    } catch {
      toast.error('Záznamy se nepodařilo načíst');
    } finally {
      setNacita(false);
    }
  }, []);

  const nactiFrontu = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/directory/queue`);
      setFronta(data);
    } catch { /* fronta může být prázdná, chybu neřešíme hláškou */ }
  }, []);

  const nactiZadosti = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/directory-claims`);
      setZadosti(data);
    } catch { /* dtto */ }
  }, []);

  useEffect(() => { nactiZaznamy(druh); }, [druh, nactiZaznamy]);
  useEffect(() => {
    nactiFrontu();
    nactiZadosti();
    axios.get(`${API}/geo/countries`).then((r) => setZeme(r.data)).catch(() => {});
  }, [nactiFrontu, nactiZadosti]);

  const rozhodni = async (zaznam, stav) => {
    try {
      await axios.put(`${API}/admin/directory/${zaznam.kind}/${zaznam.id}/status`, { status: stav });
      toast.success(stav === 'published' ? 'Zveřejněno' : 'Zamítnuto');
      nactiFrontu();
      if (zaznam.kind === druh) nactiZaznamy(druh);
    } catch {
      toast.error('Změna se nepovedla');
    }
  };

  const rozhodniZadost = async (zadost, stav) => {
    try {
      await axios.put(`${API}/admin/directory-claims/${zadost.id}`, { status: stav });
      toast.success(stav === 'approved' ? 'Profil předán do správy' : 'Zamítnuto');
      nactiZadosti();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Změna se nepovedla');
    }
  };

  const zmenOvereni = async (zaznam, hodnota) => {
    try {
      await axios.put(`${API}/admin/directory/${zaznam.kind}/${zaznam.id}/verification`,
        { verification: hodnota });
      toast.success(hodnota === 'verified' ? 'Označeno jako ověřené' : 'Ověření zrušeno');
      nactiZaznamy(druh);
    } catch {
      toast.error('Změna se nepovedla');
    }
  };

  const smaz = async (zaznam) => {
    try {
      await axios.delete(`${API}/admin/directory/${zaznam.kind}/${zaznam.id}`);
      toast.success('Smazáno');
      nactiZaznamy(druh);
      nactiFrontu();
    } catch {
      toast.error('Smazání se nepovedlo');
    }
  };

  const uloz = async () => {
    if (!formular.name.trim() || !formular.country_iso2) {
      toast.error('Vyplňte název a zemi');
      return;
    }
    setUklada(true);
    try {
      const telo = {
        name: formular.name,
        description: formular.description,
        location: {
          country_iso2: formular.country_iso2,
          region_id: formular.region_id || null,
          city_name: formular.city_name,
          address: formular.address,
        },
        email: formular.email,
        phone: formular.phone,
        social: { website: formular.website },
        categories: formular.categories.split(',').map((c) => c.trim()).filter(Boolean),
        source: formular.source,
        ...prevedDetaily(aktualni, formular.details),
      };
      await axios.post(`${API}/admin/directory/${druh}`, telo);
      toast.success('Uloženo');
      setFormularOtevren(false);
      setFormular(prazdnyFormular());
      nactiZaznamy(druh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Uložení se nepovedlo');
    } finally {
      setUklada(false);
    }
  };

  const videtelne = useMemo(() => {
    const h = hledani.trim().toLowerCase();
    return h ? zaznamy.filter((z) => z.name.toLowerCase().includes(h)) : zaznamy;
  }, [zaznamy, hledani]);

  const prepinac = (
    <div className="flex flex-wrap gap-2 border-b border-border/40 pb-4">
      {[['katalog', 'Katalog'], ['profily', 'Profily zemí']].map(([key, popisek]) => (
        <button key={key} type="button" onClick={() => setRezim(key)}
                className={`px-3.5 py-2 rounded-xl text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                  rezim === key ? 'bg-bloom-violet text-white' : 'bg-white border border-border/50 text-bloom-sub'
                }`}
                data-testid={`admin-world-rezim-${key}`}>
          {key === 'profily' ? <Globe2 className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
          {popisek}
        </button>
      ))}
    </div>
  );

  if (rezim === 'profily') {
    return (
      <div className="space-y-6" data-testid="admin-world-tab">
        {prepinac}
        <AdminCountryProfile />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-world-tab">
      {prepinac}
      {/* Žádosti o převzetí profilu. Jsou výš než návrhy záznamů schválně:
          schválení dává někomu právo měnit, co se o organizaci píše, takže to
          nesmí zapadnout mezi běžnou moderací. */}
      {zadosti.length > 0 && (
        <section className="bg-bloom-violet/5 border border-bloom-violet/20 rounded-2xl p-4"
                 data-testid="fronta-zadosti">
          <h3 className="font-heading font-bold text-bloom-text flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-bloom-violet" />
            Žádosti o převzetí profilu ({zadosti.length})
          </h3>
          <p className="mt-1 text-xs text-bloom-sub">
            Schválením dostane člověk právo upravovat údaje záznamu. Než klikneš,
            ověř si to, co uvádí – nejlépe přímo u organizace.
          </p>
          <ul className="mt-3 space-y-2">
            {zadosti.map((z) => (
              <li key={z.id} className="bg-white border border-border/40 rounded-xl p-3">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-medium text-bloom-text">{z.entry_name}</p>
                    <p className="text-xs text-bloom-sub mt-0.5">
                      {DRUHY.find((d) => d.key === z.kind)?.label}
                      {z.user?.username ? ` · žádá ${z.user.username}` : ''}
                      {z.user?.email ? ` · ${z.user.email}` : ''}
                    </p>
                    <p className="text-sm text-bloom-text mt-1.5">
                      <span className="text-bloom-sub">V organizaci je:</span> {z.role}
                    </p>
                    <p className="text-sm text-bloom-text mt-1">
                      <span className="text-bloom-sub">Ověřit lze:</span> {z.proof}
                    </p>
                    {z.contact && (
                      <p className="text-sm text-bloom-text mt-1">
                        <span className="text-bloom-sub">Kontakt:</span> {z.contact}
                      </p>
                    )}
                    {z.message && <p className="text-xs text-bloom-sub mt-1">{z.message}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => rozhodniZadost(z, 'approved')}
                            className="bg-bloom-violet hover:bg-bloom-violet/90">
                      <Check className="w-3.5 h-3.5 mr-1" />Předat
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => rozhodniZadost(z, 'rejected')}>
                      <X className="w-3.5 h-3.5 mr-1" />Zamítnout
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Fronta návrhů – nejdřív to, co čeká na rozhodnutí */}
      {fronta.length > 0 && (
        <section className="bg-amber-50/70 border border-amber-200/70 rounded-2xl p-4">
          <h3 className="font-heading font-bold text-bloom-text flex items-center gap-2">
            <Inbox className="w-4 h-4 text-amber-600" />
            Návrhy čekající na kontrolu ({fronta.length})
          </h3>
          <ul className="mt-3 space-y-2">
            {fronta.map((z) => (
              <li key={z.id} className="bg-white border border-border/40 rounded-xl p-3 flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-medium text-bloom-text">{z.name}</p>
                  <p className="text-xs text-bloom-sub mt-0.5">
                    {DRUHY.find((d) => d.key === z.kind)?.label} · {z.location?.country_iso2}
                    {z.location?.city_name ? ` · ${z.location.city_name}` : ''}
                    {z.created_by_username ? ` · navrhl/a ${z.created_by_username}` : ''}
                  </p>
                  {z.description && <p className="text-xs text-bloom-sub mt-1 line-clamp-2">{z.description}</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => rozhodni(z, 'published')}
                          className="bg-bloom-violet hover:bg-bloom-violet/90">
                    <Check className="w-3.5 h-3.5 mr-1" />Zveřejnit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => rozhodni(z, 'rejected')}>
                    <X className="w-3.5 h-3.5 mr-1" />Zamítnout
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Přepínač druhu */}
      <div className="flex flex-wrap gap-2">
        {DRUHY.map(({ key, icon: Icon, label }) => (
          <button key={key} onClick={() => setDruh(key)}
                  className={`px-3.5 py-2 rounded-xl text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                    druh === key ? 'bg-bloom-violet text-white' : 'bg-white border border-border/50 text-bloom-sub'
                  }`}
                  data-testid={`admin-world-${key}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub" />
          <Input value={hledani} onChange={(e) => setHledani(e.target.value)}
                 placeholder="Hledat v záznamech…" className="pl-9" />
        </div>
        <Button onClick={() => { setFormular(prazdnyFormular()); setFormularOtevren(true); }}
                className="bg-bloom-violet hover:bg-bloom-violet/90" data-testid="admin-world-add">
          <Plus className="w-4 h-4 mr-1" />Přidat
        </Button>
      </div>

      {nacita ? (
        <div className="py-10 flex justify-center"><div className="spinner" /></div>
      ) : videtelne.length === 0 ? (
        <p className="text-bloom-sub py-8 text-center">Zatím tu nic není.</p>
      ) : (
        <ul className="space-y-2">
          {videtelne.map((z) => (
            <li key={z.id} className="bg-white border border-border/40 rounded-xl p-3.5 flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <p className="font-medium text-bloom-text flex items-center gap-1.5">
                  {z.name}
                  {z.verification === 'verified' && <BadgeCheck className="w-4 h-4 text-bloom-mint" />}
                </p>
                <p className="text-xs text-bloom-sub mt-0.5">
                  {z.location?.country_iso2}
                  {z.location?.city_name ? ` · ${z.location.city_name}` : ''}
                  {' · '}
                  <span className={z.status === 'published' ? 'text-bloom-violet' : 'text-amber-600'}>
                    {{ published: 'zveřejněno', pending: 'čeká', rejected: 'zamítnuto', draft: 'rozpracované' }[z.status]}
                  </span>
                  {z.source ? ` · zdroj: ${z.source}` : ''}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                        onClick={() => zmenOvereni(z, z.verification === 'verified' ? 'unverified' : 'verified')}>
                  <BadgeCheck className="w-3.5 h-3.5 mr-1" />
                  {z.verification === 'verified' ? 'Zrušit ověření' : 'Ověřit'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => smaz(z)}
                        className="text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-bloom-sub/70">
        Ověření znamená, že záznam odpovídá skutečnosti — ne že ho Bloom doporučuje.
      </p>

      <Dialog open={formularOtevren} onOpenChange={setFormularOtevren}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Přidat: {aktualni?.label}</DialogTitle>
            <DialogDescription>
              Záznam se zveřejní hned. Vyplňte prosím i zdroj — u adres a termínů
              se to při pozdější kontrole vyplatí.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Pole label="Název *">
              <Input value={formular.name} onChange={(e) => setFormular({ ...formular, name: e.target.value })} />
            </Pole>
            <Pole label="Popis">
              <Textarea rows={3} value={formular.description}
                        onChange={(e) => setFormular({ ...formular, description: e.target.value })} />
            </Pole>
            <div className="grid sm:grid-cols-2 gap-3">
              <Pole label="Země *">
                <select value={formular.country_iso2}
                        onChange={(e) => setFormular({ ...formular, country_iso2: e.target.value })}
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">— vyberte —</option>
                  {zeme.map((z) => <option key={z.iso2} value={z.iso2}>{z.name_cs}</option>)}
                </select>
              </Pole>
              <Pole label="Město">
                <Input value={formular.city_name}
                       onChange={(e) => setFormular({ ...formular, city_name: e.target.value })} />
              </Pole>
            </div>
            <RegionSelect countryIso2={formular.country_iso2}
                          hodnota={formular.region_id}
                          onChange={(v) => setFormular({ ...formular, region_id: v })} />

            <Pole label="Adresa">
              <Input value={formular.address}
                     onChange={(e) => setFormular({ ...formular, address: e.target.value })} />
            </Pole>
            <div className="grid sm:grid-cols-3 gap-3">
              <Pole label="Web"><Input value={formular.website}
                     onChange={(e) => setFormular({ ...formular, website: e.target.value })} /></Pole>
              <Pole label="E-mail"><Input value={formular.email}
                     onChange={(e) => setFormular({ ...formular, email: e.target.value })} /></Pole>
              <Pole label="Telefon"><Input value={formular.phone}
                     onChange={(e) => setFormular({ ...formular, phone: e.target.value })} /></Pole>
            </div>
            <Pole label="Kategorie (oddělte čárkou)">
              <Input value={formular.categories}
                     onChange={(e) => setFormular({ ...formular, categories: e.target.value })} />
            </Pole>

            {aktualni?.pole.map((p) => (
              <Pole key={p.name} label={p.label + (p.povinne ? ' *' : '')}>
                <PoleDetailu pole={p} hodnota={formular.details[p.name] || ''}
                             onChange={(v) => setFormular({
                               ...formular, details: { ...formular.details, [p.name]: v },
                             })} />
              </Pole>
            ))}

            <Pole label="Zdroj informace">
              <Input value={formular.source} placeholder="např. web organizace, e-mail od pořadatele…"
                     onChange={(e) => setFormular({ ...formular, source: e.target.value })} />
            </Pole>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setFormularOtevren(false)}>Zrušit</Button>
            <Button onClick={uloz} disabled={uklada} className="bg-bloom-violet hover:bg-bloom-violet/90">
              {uklada ? 'Ukládám…' : 'Uložit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Pole({ label, children }) {
  return (
    <div>
      <Label className="text-sm text-bloom-text">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function PoleDetailu({ pole, hodnota, onChange }) {
  if (pole.typ === 'vyber') {
    return (
      <select value={hodnota} onChange={(e) => onChange(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
        <option value="">— vyberte —</option>
        {pole.moznosti.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    );
  }
  if (pole.typ === 'datum') {
    return <Input type="datetime-local" value={hodnota} onChange={(e) => onChange(e.target.value)} />;
  }
  if (pole.typ === 'dlouhy') {
    return <Textarea rows={4} value={hodnota} onChange={(e) => onChange(e.target.value)} />;
  }
  if (pole.typ === 'seznam') {
    return <Input value={hodnota} placeholder="oddělte čárkou"
                  onChange={(e) => onChange(e.target.value)} />;
  }
  return <Input value={hodnota} onChange={(e) => onChange(e.target.value)} />;
}

// Detaily se posílají naplocho vedle ostatních polí – server si je sám roztřídí.
function prevedDetaily(druh, detaily) {
  const vystup = {};
  (druh?.pole || []).forEach((p) => {
    const v = detaily[p.name];
    if (v === undefined || v === '') return;
    if (p.typ === 'seznam') {
      vystup[p.name] = String(v).split(',').map((x) => x.trim()).filter(Boolean);
    } else if (p.typ === 'datum') {
      vystup[p.name] = new Date(v).toISOString();
    } else {
      vystup[p.name] = v;
    }
  });
  return vystup;
}
